import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, getDocs, addDoc, deleteDoc,
  doc, updateDoc, serverTimestamp, storage, ref, uploadBytes, getDownloadURL
} from '../../firebase/config';
import {
  Plus, Edit2, Trash2, X, Camera, Package,
  Search, AlertCircle, Video, Palette, Ruler,
  Wind, Tag, Scissors
} from 'lucide-react';

const MAX_MEDIA = 5;
const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_QUALITY = 0.82;

// ============================================================
// IMAGE COMPRESSION
// ============================================================
const compressImage = (file) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          if (width >= height) {
            height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
            width = MAX_IMAGE_DIMENSION;
          } else {
            width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
            height = MAX_IMAGE_DIMENSION;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            const baseName = file.name.replace(/\.[^.]+$/, '');
            const compressed = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressed);
          },
          'image/jpeg',
          IMAGE_QUALITY
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

// ============================================================
// CATEGORY CONFIG
// ============================================================
const CATEGORY_CONFIG = {
  'Skincare':        { attributeKeys: [] },
  'Makeup':          { attributeKeys: ['colors'] },
  'Haircare':        { attributeKeys: [] },
  'Bodycare':        { attributeKeys: [] },
  'Tools':           { attributeKeys: [] },
  'Fragrance':       { attributeKeys: ['fragrance'] },
  'Hair Extensions': { attributeKeys: ['colors', 'lengths', 'hairType', 'hairpiece'] },
  'Bundles':         { attributeKeys: ['colors', 'lengths', 'hairType', 'hairpiece', 'bundle'] },
  'Wigs':            { attributeKeys: ['colors', 'lengths', 'hairType', 'hairpiece'] },
  'Hairpieces':      { attributeKeys: ['colors', 'lengths', 'hairType', 'hairpiece'] },
  'Accessories':     { attributeKeys: ['colors'] },
  'Other':           { attributeKeys: ['custom'] },
};

const CATEGORY_SUGGESTIONS = Object.keys(CATEGORY_CONFIG);

const LENGTH_PRESETS = ['10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"', '32"'];
const HAIR_TYPES = [
  { id: 'human', label: '100% Human Hair' },
  { id: 'synthetic', label: 'Synthetic' },
  { id: 'blend', label: 'Blend' },
];
const HAIRPIECE_TYPES = [
  { id: 'bundle', label: 'Bundle' },
  { id: 'weave', label: 'Weave' },
  { id: 'closure', label: 'Closure' },
  { id: 'frontal', label: 'Frontal' },
  { id: 'wig', label: 'Wig' },
  { id: 'hairpiece', label: 'Hairpiece' },
];
const FRAGRANCE_FAMILIES = [
  'Floral', 'Woody', 'Oriental', 'Fresh', 'Gourmand', 'Citrus', 'Aquatic', 'Spicy',
];

const ProfessionalProductsPage = () => {
  const { user, userData } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const fileInputRef = useRef(null);

  const [mediaItems, setMediaItems] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: '',
    price: '',
    stock: '',
    description: '',
    ingredients: '',
  });

  const [attributes, setAttributes] = useState({
    colors: [],
    lengths: [],
    hairType: '',
    hairpieceType: '',
    bundleCount: '',
    bundleWeight: '',
    fragranceFamily: '',
    fragranceNotes: '',
    fragranceSize: '',
    customAttributes: [],
  });

  const [newColorName, setNewColorName] = useState('');
  const [newColorHex, setNewColorHex] = useState('#000000');
  const [newLength, setNewLength] = useState('');
  const [newCustomKey, setNewCustomKey] = useState('');
  const [newCustomValue, setNewCustomValue] = useState('');

  useEffect(() => {
    if (!user) return;
    loadProducts();
  }, [user]);

  const loadProducts = async () => {
    try {
      const q = query(collection(db, 'products'), where('professionalId', '==', user.uid));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const matchedCategoryKey = Object.keys(CATEGORY_CONFIG).find(
    k => k.toLowerCase() === (formData.category || '').toLowerCase().trim()
  );
  const activeAttributeKeys = matchedCategoryKey
    ? CATEGORY_CONFIG[matchedCategoryKey].attributeKeys
    : [];

  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = MAX_MEDIA - mediaItems.length;
    if (remainingSlots <= 0) {
      alert(`You can upload up to ${MAX_MEDIA} files`);
      return;
    }

    const toAdd = files.slice(0, remainingSlots);

    for (const file of toAdd) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      if (!isVideo && !isImage) continue;

      const limit = isVideo ? 25 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > limit) {
        alert(`${file.name} is too large (max ${isVideo ? '25MB' : '5MB'})`);
        continue;
      }

      let processed = file;
      if (isImage) {
        try {
          processed = await compressImage(file);
        } catch (err) {
          console.warn('Compression failed:', err);
        }
      }

      const previewUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(processed);
      });

      setMediaItems(prev => [
        ...prev,
        { file: processed, url: previewUrl, type: isVideo ? 'video' : 'image', isNew: true },
      ]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMedia = (index) => {
    setMediaItems(prev => prev.filter((_, i) => i !== index));
  };

  const makeCover = (index) => {
    setMediaItems(prev => {
      const next = [...prev];
      const [picked] = next.splice(index, 1);
      next.unshift(picked);
      return next;
    });
  };

  const uploadAllMedia = async () => {
    const result = [];
    for (const item of mediaItems) {
      if (item.isNew && item.file) {
        const storageRef = ref(storage, `products/${user.uid}/${Date.now()}_${item.file.name}`);
        await uploadBytes(storageRef, item.file, {
          contentType: item.file.type || 'image/jpeg',
        });
        const url = await getDownloadURL(storageRef);
        result.push({ url, type: item.type });
      } else {
        result.push({ url: item.url, type: item.type });
      }
    }
    return result;
  };

  const addColor = () => {
    const name = newColorName.trim();
    if (!name) { alert('Enter a colour name'); return; }
    if (attributes.colors.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      alert('Colour already added');
      return;
    }
    setAttributes(prev => ({
      ...prev,
      colors: [...prev.colors, { name, hex: newColorHex }],
    }));
    setNewColorName('');
    setNewColorHex('#000000');
  };

  const removeColor = (name) => {
    setAttributes(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c.name !== name),
    }));
  };

  const toggleLength = (len) => {
    setAttributes(prev => ({
      ...prev,
      lengths: prev.lengths.includes(len)
        ? prev.lengths.filter(l => l !== len)
        : [...prev.lengths, len],
    }));
  };

  const addCustomLength = () => {
    const val = newLength.trim();
    if (!val) return;
    if (attributes.lengths.includes(val)) return;
    setAttributes(prev => ({ ...prev, lengths: [...prev.lengths, val] }));
    setNewLength('');
  };

  const addCustomAttribute = () => {
    const k = newCustomKey.trim();
    const v = newCustomValue.trim();
    if (!k || !v) { alert('Enter both a name and value'); return; }
    setAttributes(prev => ({
      ...prev,
      customAttributes: [...prev.customAttributes, { key: k, value: v }],
    }));
    setNewCustomKey('');
    setNewCustomValue('');
  };

  const removeCustomAttribute = (idx) => {
    setAttributes(prev => ({
      ...prev,
      customAttributes: prev.customAttributes.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price || !formData.stock) {
      alert('Please fill in required fields');
      return;
    }

    setUploading(true);
    try {
      const uploadedMedia = await uploadAllMedia();
      const coverUrl = uploadedMedia[0]?.url || null;

      const cleanAttrs = {};
      if (attributes.colors.length > 0) cleanAttrs.colors = attributes.colors;
      if (attributes.lengths.length > 0) cleanAttrs.lengths = attributes.lengths;
      if (attributes.hairType) cleanAttrs.hairType = attributes.hairType;
      if (attributes.hairpieceType) cleanAttrs.hairpieceType = attributes.hairpieceType;
      if (attributes.bundleCount) cleanAttrs.bundleCount = Number(attributes.bundleCount);
      if (attributes.bundleWeight) cleanAttrs.bundleWeight = attributes.bundleWeight;
      if (attributes.fragranceFamily) cleanAttrs.fragranceFamily = attributes.fragranceFamily;
      if (attributes.fragranceNotes) cleanAttrs.fragranceNotes = attributes.fragranceNotes;
      if (attributes.fragranceSize) cleanAttrs.fragranceSize = attributes.fragranceSize;
      if (attributes.customAttributes.length > 0) cleanAttrs.customAttributes = attributes.customAttributes;

      const productData = {
        name: formData.name,
        brand: formData.brand,
        category: formData.category,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        description: formData.description,
        ingredients: formData.ingredients,
        media: uploadedMedia,
        mediaLegacy: uploadedMedia[0] || null,
        image: coverUrl,
        attributes: cleanAttrs,
        professionalId: user.uid,
        professionalName: userData?.businessName || userData?.name || 'Professional',
        professionalImage: userData?.profileImage || userData?.photoURL || null,
        updatedAt: serverTimestamp(),
      };

      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
      } else {
        await addDoc(collection(db, 'products'), {
          ...productData,
          createdAt: serverTimestamp(),
          active: true,
        });
      }

      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert(`Failed to save product: ${error.code || ''} ${error.message || ''}`);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', brand: '', category: '', price: '', stock: '',
      description: '', ingredients: '',
    });
    setAttributes({
      colors: [], lengths: [], hairType: '', hairpieceType: '',
      bundleCount: '', bundleWeight: '',
      fragranceFamily: '', fragranceNotes: '', fragranceSize: '',
      customAttributes: [],
    });
    setNewColorName('');
    setNewColorHex('#000000');
    setNewLength('');
    setNewCustomKey('');
    setNewCustomValue('');
    setMediaItems([]);
    setEditingProduct(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      brand: product.brand || '',
      category: product.category || '',
      price: product.price || '',
      stock: product.stock || '',
      description: product.description || '',
      ingredients: product.ingredients || '',
    });

    const a = product.attributes || {};
    setAttributes({
      colors: Array.isArray(a.colors) ? a.colors : [],
      lengths: Array.isArray(a.lengths) ? a.lengths : [],
      hairType: a.hairType || '',
      hairpieceType: a.hairpieceType || '',
      bundleCount: a.bundleCount ?? '',
      bundleWeight: a.bundleWeight || '',
      fragranceFamily: a.fragranceFamily || '',
      fragranceNotes: a.fragranceNotes || '',
      fragranceSize: a.fragranceSize || '',
      customAttributes: Array.isArray(a.customAttributes) ? a.customAttributes : [],
    });

    let loaded = [];
    if (Array.isArray(product.media) && product.media.length > 0) {
      loaded = product.media.map(m => ({ url: m.url, type: m.type || 'image', isNew: false }));
    } else if (product.mediaLegacy?.url) {
      loaded = [{ url: product.mediaLegacy.url, type: product.mediaLegacy.type || 'image', isNew: false }];
    } else if (product.image) {
      loaded = [{ url: product.image, type: 'image', isNew: false }];
    }
    setMediaItems(loaded);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const getCoverMedia = (product) => {
    if (Array.isArray(product.media) && product.media.length > 0) return product.media[0];
    if (product.mediaLegacy?.url) return product.mediaLegacy;
    if (product.image) return { url: product.image, type: 'image' };
    return null;
  };

  const getMediaCount = (product) => {
    if (Array.isArray(product.media)) return product.media.length;
    if (product.mediaLegacy?.url || product.image) return 1;
    return 0;
  };

  const totalValue = products.reduce((s, p) => s + (p.price || 0) * (p.stock || 0), 0);
  const lowStockCount = products.filter(p => p.stock > 0 && p.stock < 5).length;
  const outOfStockCount = products.filter(p => p.stock === 0).length;

  const filteredProducts = products
    .filter(p => {
      if (!searchTerm) return true;
      const t = searchTerm.toLowerCase();
      return (p.name?.toLowerCase().includes(t) || p.brand?.toLowerCase().includes(t) || p.category?.toLowerCase().includes(t));
    })
    .sort((a, b) => {
      if (sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'price-low') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'stock-low') return (a.stock || 0) - (b.stock || 0);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-24 sm:pb-8">
      {/* HEADER */}
      <div className="bg-[#F4EEFF] rounded-3xl p-6 md:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">Your Storefront</span>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">Products</h1>
            <p className="text-gray-500 mt-1.5 text-sm">
              {products.length > 0
                ? `${products.length} ${products.length === 1 ? 'item' : 'items'} in your catalogue`
                : 'Curate your product collection'}
            </p>
          </div>
          
          {/* Main Desktop Button (Becomes full-width and larger on Mobile) */}
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="w-full sm:w-auto px-6 py-4 sm:py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-transform active:scale-[0.98] flex items-center justify-center gap-2 shadow-md touch-manipulation"
          >
            <Plus size={18} /> Add Product
          </button>
        </div>

        {products.length > 0 && (
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <Package size={14} className="text-[#AF8BD4]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Items</span>
              </div>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{products.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Value</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">
                R{totalValue.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={14} className="text-[#E88B8B]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Attention</span>
              </div>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{lowStockCount + outOfStockCount}</p>
            </div>
          </div>
        )}
      </div>

      {/* MOBILE FLOATING ACTION BUTTON */}
      {products.length > 0 && (
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="sm:hidden fixed bottom-[88px] right-5 w-14 h-14 bg-[#010101] text-white rounded-full shadow-2xl flex items-center justify-center z-40 active:scale-95 transition-transform touch-manipulation"
          aria-label="Add Product"
        >
          <Plus size={24} />
        </button>
      )}

      {/* FILTER BAR */}
      {products.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-white border border-gray-100 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-gray-100 rounded-full px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] cursor-pointer"
          >
            <option value="newest">Newest first</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
            <option value="stock-low">Stock: Low to High</option>
          </select>
        </div>
      )}

      {/* PRODUCTS GRID */}
      {products.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50 px-4">
          <div className="w-24 h-24 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-5">
            <Package size={36} className="text-[#AF8BD4]" />
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Your storefront awaits</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Add your first product to start selling directly to clients on Skytone.
          </p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="w-full sm:w-auto px-6 py-4 sm:py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-transform active:scale-[0.98] flex items-center justify-center gap-2 shadow-md touch-manipulation"
          >
            <Plus size={18} /> Add Your First Product
          </button>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl border border-gray-50">
          <p className="text-gray-400 text-sm">No products match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const cover = getCoverMedia(product);
            const mediaCount = getMediaCount(product);
            const isOut = product.stock === 0;
            const isLow = product.stock > 0 && product.stock < 5;
            const colorSwatches = Array.isArray(product.attributes?.colors) ? product.attributes.colors : [];
            return (
              <div
                key={product.id}
                className="bg-white rounded-3xl p-3 border border-gray-100 hover:border-[#CDBCE7] transition-colors group"
              >
                <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-[#FCF8F9] mb-3">
                  {cover ? (
                    cover.type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video size={40} className="text-gray-300" />
                      </div>
                    ) : (
                      <img src={cover.url} alt={product.name} className="w-full h-full object-cover" />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={40} className="text-gray-200" />
                    </div>
                  )}

                  {mediaCount > 1 && (
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      +{mediaCount - 1} photo{mediaCount - 1 > 1 ? 's' : ''}
                    </div>
                  )}

                  {/* Larger Edit/Delete touch targets on mobile */}
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(product)}
                      className="w-10 h-10 sm:w-9 sm:h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 active:scale-95 touch-manipulation"
                      aria-label="Edit Product"
                    >
                      <Edit2 size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="w-10 h-10 sm:w-9 sm:h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50 active:scale-95 touch-manipulation"
                      aria-label="Delete Product"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>

                  {(isLow || isOut) && (
                    <div className={`absolute bottom-2 left-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                      isOut ? 'bg-gray-900 text-white' : 'bg-[#FFF3E8] text-[#B87333]'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isOut ? 'bg-white' : 'bg-[#E8B48E]'}`}></span>
                      {isOut ? 'Sold Out' : `Only ${product.stock} left`}
                    </div>
                  )}
                </div>

                <div className="px-2">
                  {product.category && (
                    <span className="text-[10px] font-bold tracking-wider text-[#AF8BD4] uppercase">{product.category}</span>
                  )}
                  <h3 className="font-bold text-gray-900 text-sm truncate mt-0.5">{product.name}</h3>
                  {product.brand && <p className="text-xs text-gray-400 truncate">{product.brand}</p>}

                  {colorSwatches.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      {colorSwatches.slice(0, 5).map(c => (
                        <div
                          key={c.name}
                          title={c.name}
                          className="w-4 h-4 rounded-full border border-gray-200"
                          style={{ backgroundColor: c.hex }}
                        />
                      ))}
                      {colorSwatches.length > 5 && (
                        <span className="text-[10px] text-gray-400">+{colorSwatches.length - 5}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Price</p>
                      <p className="font-bold text-gray-900">R{product.price?.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Stock</p>
                      <p className="font-bold text-gray-900">{product.stock}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col sm:flex-row">

            {/* LEFT: MEDIA */}
            <div className="sm:w-[38%] bg-[#F4EEFF] p-4 sm:p-5 flex flex-col shrink-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">
                  Media · {mediaItems.length}/{MAX_MEDIA}
                </span>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="sm:hidden w-10 h-10 rounded-full bg-white flex items-center justify-center active:scale-95 touch-manipulation shadow-sm"
                >
                  <X size={20} className="text-gray-700" />
                </button>
              </div>

              <div className="relative w-full rounded-2xl overflow-hidden bg-white mb-3 aspect-[4/3] max-h-[180px]">
                {mediaItems[0] ? (
                  <>
                    {mediaItems[0].type === 'video' ? (
                      <video src={mediaItems[0].url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={mediaItems[0].url} className="w-full h-full object-cover" alt="Cover" />
                    )}
                    <span className="absolute top-2 left-2 bg-[#010101] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Cover
                    </span>
                    <button
                      type="button"
                      onClick={() => removeMedia(0)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md active:scale-95 touch-manipulation"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-full flex flex-col items-center justify-center text-[#AF8BD4] hover:bg-[#FCF8F9] transition-colors active:scale-[0.98] touch-manipulation"
                  >
                    <Camera size={24} />
                    <span className="text-[11px] font-bold uppercase tracking-widest mt-2">Add Media</span>
                    <span className="text-[10px] text-gray-400 mt-0.5">Up to {MAX_MEDIA} files</span>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {mediaItems.slice(1).map((item, i) => {
                  const realIndex = i + 1;
                  return (
                    <div key={realIndex} className="relative w-14 h-14 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-white shrink-0">
                      <button
                        type="button"
                        onClick={() => makeCover(realIndex)}
                        className="w-full h-full active:scale-95 touch-manipulation"
                        title="Set as cover"
                      >
                        {item.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center bg-[#FCF8F9]">
                            <Video size={16} className="text-gray-500" />
                          </div>
                        ) : (
                          <img src={item.url} className="w-full h-full object-cover" alt="" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeMedia(realIndex); }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] active:scale-95 touch-manipulation shadow-sm"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}

                {mediaItems.length < MAX_MEDIA && mediaItems.length > 0 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-14 h-14 sm:w-12 sm:h-12 rounded-lg border-2 border-dashed border-[#CDBCE7] flex items-center justify-center text-[#AF8BD4] hover:bg-white transition-colors shrink-0 active:scale-95 touch-manipulation"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>

              {mediaItems.length > 1 && (
                <p className="text-[10px] text-gray-500 mt-2">Tap a thumbnail to set as cover</p>
              )}

              <p className="text-[9px] text-gray-400 mt-3 leading-relaxed">
                Images auto-compress before upload.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFilesSelect}
                className="hidden"
              />
            </div>

            {/* RIGHT: FORM */}
            <div className="sm:w-[62%] flex flex-col max-h-[95vh] sm:max-h-[90vh] min-h-0 bg-white">
              <div className="p-5 sm:p-6 pb-4 border-b border-gray-50 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="font-serif font-bold text-xl sm:text-2xl text-gray-900">
                    {editingProduct ? 'Edit Product' : 'New Product'}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {editingProduct ? 'Update details below' : 'Fill in the details below'}
                  </p>
                </div>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="hidden sm:flex w-9 h-9 rounded-full bg-[#FCF8F9] items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                    placeholder="e.g. Vitamin C Brightening Serum"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Brand</label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                      placeholder="The Ordinary"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Price (R) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                      placeholder="299.00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                    placeholder="Type or pick below"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {CATEGORY_SUGGESTIONS.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat })}
                        className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                          formData.category === cat
                            ? 'bg-[#010101] text-white border-[#010101]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                    placeholder="50"
                    required
                  />
                </div>

                {/* ============================================================ */}
                {/* CONDITIONAL ATTRIBUTES — based on category                    */}
                {/* ============================================================ */}

                {activeAttributeKeys.includes('colors') && (
                  <div className="border-t border-gray-100 pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Palette size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Available colours
                      </label>
                    </div>

                    {attributes.colors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {attributes.colors.map(c => (
                          <div key={c.name} className="flex items-center gap-2 bg-[#FCF8F9] rounded-full pl-2 pr-2 py-1.5">
                            <div
                              className="w-4 h-4 rounded-full border border-gray-200 shrink-0"
                              style={{ backgroundColor: c.hex }}
                            />
                            <span className="text-[11px] font-bold text-gray-700">{c.name}</span>
                            <button
                              type="button"
                              onClick={() => removeColor(c.name)}
                              className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center hover:bg-red-100 touch-manipulation active:scale-95"
                            >
                              <X size={12} className="text-gray-600" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={newColorHex}
                        onChange={(e) => setNewColorHex(e.target.value)}
                        className="w-14 h-12 rounded-lg border border-gray-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={newColorName}
                        onChange={(e) => setNewColorName(e.target.value)}
                        placeholder="e.g. Jet Black"
                        className="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); addColor(); }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addColor}
                        className="px-4 rounded-lg bg-[#AF8BD4] text-white font-bold text-sm hover:bg-[#9C77C9] shrink-0 touch-manipulation active:scale-95"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('lengths') && (
                  <div className="border-t border-gray-100 pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Ruler size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Available lengths
                      </label>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {LENGTH_PRESETS.map(len => {
                        const selected = attributes.lengths.includes(len);
                        return (
                          <button
                            key={len}
                            type="button"
                            onClick={() => toggleLength(len)}
                            className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                              selected
                                ? 'bg-[#010101] text-white border-[#010101]'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                            }`}
                          >
                            {len}
                          </button>
                        );
                      })}
                    </div>

                    {attributes.lengths.filter(l => !LENGTH_PRESETS.includes(l)).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {attributes.lengths.filter(l => !LENGTH_PRESETS.includes(l)).map(len => (
                          <div key={len} className="flex items-center gap-1.5 bg-[#F4EEFF] rounded-full pl-3 pr-1.5 py-1.5">
                            <span className="text-[11px] font-bold text-[#AF8BD4]">{len}</span>
                            <button
                              type="button"
                              onClick={() => toggleLength(len)}
                              className="w-5 h-5 rounded-full bg-white flex items-center justify-center touch-manipulation active:scale-95"
                            >
                              <X size={12} className="text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newLength}
                        onChange={(e) => setNewLength(e.target.value)}
                        placeholder='Custom (e.g. 34")'
                        className="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); addCustomLength(); }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addCustomLength}
                        className="px-4 rounded-lg bg-[#AF8BD4] text-white font-bold text-sm hover:bg-[#9C77C9] shrink-0 touch-manipulation active:scale-95"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('hairType') && (
                  <div className="border-t border-gray-100 pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Scissors size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Hair type
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {HAIR_TYPES.map(h => (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => setAttributes(prev => ({ ...prev, hairType: h.id }))}
                          className={`px-4 py-2.5 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                            attributes.hairType === h.id
                              ? 'bg-[#010101] text-white border-[#010101]'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                          }`}
                        >
                          {h.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('hairpiece') && (
                  <div className="border-t border-gray-100 pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Hairpiece type
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {HAIRPIECE_TYPES.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setAttributes(prev => ({
                            ...prev,
                            hairpieceType: prev.hairpieceType === t.id ? '' : t.id,
                          }))}
                          className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                            attributes.hairpieceType === t.id
                              ? 'bg-[#010101] text-white border-[#010101]'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('bundle') && (
                  <div className="border-t border-gray-100 pt-5">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-2">
                      Bundle details
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 block mb-1.5">Bundle count</label>
                        <input
                          type="number"
                          min="1"
                          value={attributes.bundleCount}
                          onChange={(e) => setAttributes(prev => ({ ...prev, bundleCount: e.target.value }))}
                          placeholder="3"
                          className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 block mb-1.5">Weight per bundle</label>
                        <input
                          type="text"
                          value={attributes.bundleWeight}
                          onChange={(e) => setAttributes(prev => ({ ...prev, bundleWeight: e.target.value }))}
                          placeholder="100g"
                          className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('fragrance') && (
                  <div className="border-t border-gray-100 pt-5 space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Wind size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Fragrance details
                      </label>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-gray-400 block mb-2">Family</label>
                      <div className="flex flex-wrap gap-2">
                        {FRAGRANCE_FAMILIES.map(f => (
                          <button
                            key={f}
                            type="button"
                            onClick={() => setAttributes(prev => ({
                              ...prev,
                              fragranceFamily: prev.fragranceFamily === f ? '' : f,
                            }))}
                            className={`px-4 py-2 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                              attributes.fragranceFamily === f
                                ? 'bg-[#010101] text-white border-[#010101]'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-gray-400 block mb-2">Bottle size</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={attributes.fragranceSize}
                          onChange={(e) => setAttributes(prev => ({ ...prev, fragranceSize: e.target.value }))}
                          placeholder="e.g. 50ml"
                          className="flex-1 bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        />
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {['30ml', '50ml', '100ml', '150ml', '200ml'].map(size => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => setAttributes(prev => ({ ...prev, fragranceSize: size }))}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors touch-manipulation active:scale-95 ${
                              attributes.fragranceSize === size
                                ? 'bg-[#010101] text-white border-[#010101]'
                                : 'bg-white text-gray-600 border-gray-200'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-gray-400 block mb-2">Notes</label>
                      <textarea
                        rows="3"
                        value={attributes.fragranceNotes}
                        onChange={(e) => setAttributes(prev => ({ ...prev, fragranceNotes: e.target.value }))}
                        placeholder="Top: bergamot · Heart: rose · Base: amber"
                        className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] resize-none"
                      />
                    </div>
                  </div>
                )}

                {activeAttributeKeys.includes('custom') && (
                  <div className="border-t border-gray-100 pt-5">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-3">
                      Custom details
                    </label>

                    {attributes.customAttributes.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {attributes.customAttributes.map((attr, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-[#FCF8F9] rounded-lg p-3">
                            <span className="text-[11px] font-bold text-gray-700">{attr.key}</span>
                            <span className="text-[11px] text-gray-500 flex-1">{attr.value}</span>
                            <button
                              type="button"
                              onClick={() => removeCustomAttribute(idx)}
                              className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-red-500 touch-manipulation active:scale-95 shadow-sm"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCustomKey}
                        onChange={(e) => setNewCustomKey(e.target.value)}
                        placeholder="Name"
                        className="w-1/3 bg-white border border-gray-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                      />
                      <input
                        type="text"
                        value={newCustomValue}
                        onChange={(e) => setNewCustomValue(e.target.value)}
                        placeholder="Value"
                        className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                      />
                      <button
                        type="button"
                        onClick={addCustomAttribute}
                        className="px-4 rounded-lg bg-[#AF8BD4] text-white font-bold text-sm hover:bg-[#9C77C9] shrink-0 touch-manipulation active:scale-95"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Description & Ingredients */}
                <div className="border-t border-gray-100 pt-5">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="4"
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors resize-none"
                    placeholder="Describe your product..."
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Ingredients</label>
                  <textarea
                    value={formData.ingredients}
                    onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                    rows="3"
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors resize-none"
                    placeholder="Key ingredients..."
                  />
                </div>
              </form>

              <div className="p-5 sm:p-6 pt-4 border-t border-gray-50 shrink-0">
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="w-full bg-[#010101] text-white py-4 sm:py-3.5 rounded-full font-bold text-sm hover:bg-black/90 transition-transform active:scale-[0.98] touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>{editingProduct ? 'Save Changes' : 'Publish Product'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalProductsPage;